import type { StatusOverview } from '@/mocks/adminSettingsRadio';

interface Props {
  status: StatusOverview;
  onCopyUrl: () => void;
  onDownloadQr: () => void;
  onPreview: () => void;
}

export default function StatusOverview({ status, onCopyUrl, onDownloadQr, onPreview }: Props) {
  const cards = [
    {
      icon: 'ri-broadcast-line',
      label: 'Frecuencia',
      dot: status.frecuencia.estado === 'online' ? 'bg-emerald-500 animate-pulse' : 'bg-red-400',
      text: status.frecuencia.label,
      bg: 'bg-background-50',
      border: 'border-background-200',
    },
    {
      icon: 'ri-global-line',
      label: 'Dominio',
      dot: status.dominio.estado === 'activo' ? 'bg-emerald-500' : 'bg-amber-500',
      text: status.dominio.label,
      bg: status.dominio.colorClass.split(' ').find(c => c.startsWith('bg-')) || 'bg-background-50',
      border: status.dominio.colorClass.split(' ').find(c => c.startsWith('border-')) || 'border-background-200',
      textColor: status.dominio.colorClass.split(' ').find(c => c.startsWith('text-')) || 'text-foreground-800',
    },
    {
      icon: 'ri-vip-crown-line',
      label: 'Plan',
      dot: 'bg-primary-500',
      text: status.plan.label,
      bg: 'bg-primary-50',
      border: 'border-primary-200',
      textColor: 'text-primary-800',
    },
    {
      icon: 'ri-shield-check-line',
      label: 'Licencia Anual',
      dot: status.licencia.estado === 'vigente' ? 'bg-emerald-500' : 'bg-red-500',
      text: `${status.licencia.label} — ${status.licencia.vencimiento}`,
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      textColor: 'text-emerald-800',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Status grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((card) => (
          <div key={card.label} className={`p-4 rounded-2xl border ${card.bg} ${card.border} flex flex-col gap-2`}>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-background-200/70 flex items-center justify-center">
                <i className={`${card.icon} text-foreground-500 text-sm`}></i>
              </div>
              <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">{card.label}</span>
              <span className={`w-2 h-2 rounded-full ml-auto shrink-0 ${card.dot}`}></span>
            </div>
            <p className={`text-xs font-medium leading-snug ${card.textColor || 'text-foreground-800'}`}>{card.text}</p>
          </div>
        ))}
      </div>

      {/* Global action buttons */}
      <div className="flex flex-wrap items-center gap-2 p-4 rounded-2xl bg-background-50 border border-background-200">
        <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mr-1">Acciones rápidas</span>
        <button
          onClick={onPreview}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foreground-950 text-background-50 text-[10px] font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-eye-line text-xs"></i>
          Vista previa
        </button>
        <button
          onClick={onCopyUrl}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-[10px] font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-file-copy-line text-xs"></i>
          Copiar URL
        </button>
        <button
          onClick={onDownloadQr}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-[10px] font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-download-line text-xs"></i>
          Descargar QR
        </button>
      </div>
    </div>
  );
}